import { MetricCard, type MetricDelta } from "@fox1ck-org/ui";

import type { Bag } from "@/lib/campaigns-table";
import { DASH, formatChange, formatInt, formatMoney, formatPct, formatPp } from "@/lib/format";
import { useT } from "@/lib/i18n";

/** What a metric costs when it grows: spend and prices per unit go the bad way up. */
const COSTLY = new Set(["cost", "cpa", "cpc", "cpl", "bots", "bots_pct"]);

/**
 * The change of one card number against the comparison window, in the
 * words the board uses: a percent for counts and money, the difference for
 * profit, percentage points for ROI. Undefined when there is nothing to
 * compare with — «no change» and «not measured» are different (MetricCard).
 * Pure.
 */
export function cardDelta(key: string, kind: "count" | "profit" | "pp", cur: number | undefined, prev: number | undefined): MetricDelta | undefined {
  if (cur === undefined || prev === undefined || !Number.isFinite(cur) || !Number.isFinite(prev)) return undefined;
  const value = kind === "pp" ? formatPp(cur - prev) : kind === "profit" ? formatMoney(cur - prev, { signed: true }) : formatChange(cur, prev);
  if (value === DASH) return undefined;
  const direction = cur === prev ? "flat" : cur > prev ? "up" : "down";
  return { value, direction, good: COSTLY.has(key) ? "down" : "up" };
}

/** «+» for a positive amount, the status ink for profit and ROI. Pure. */
function signedTone(v: number | undefined): "ok" | "danger" | "neutral" {
  return v === undefined || v === 0 ? "neutral" : v > 0 ? "ok" : "danger";
}

/**
 * Клики · Лиды · Апрувы · В холде · Доход · Расход · Профит · ROI (доска
 * CSummary): the campaign's row of the list over the Top period, each with
 * its change against the comparison window and one line of context. One
 * `MetricCard` per number, drawn as a joined strip. `empty` is the new
 * campaign's strip (доска CEmpty): dashes and what each is waiting for.
 */
export function CardKpis({ v, prev, loading, empty }: { v: Bag | undefined; prev: Bag | undefined; loading: boolean; empty?: boolean }) {
  const t = useT();
  const tiles: { key: string; label: string; value: string; delta?: MetricDelta; note?: string; tone?: "ok" | "danger" | "neutral" }[] = empty
    ? [
        { key: "clicks", label: t("cols.clicks"), value: DASH, note: t("card.kpi.waitFirst") },
        { key: "leads", label: t("cols.leads"), value: DASH, note: t("kpi.cr", { cr: DASH }) },
        { key: "conversions", label: t("cols.approved"), value: DASH, note: DASH },
        { key: "hold", label: t("kpi.hold"), value: DASH, note: DASH },
        { key: "revenue", label: t("cols.revenue"), value: DASH, note: t("kpi.epc", { epc: DASH }) },
        { key: "cost", label: t("cols.cost"), value: DASH, note: t("card.kpi.costFromSource") },
        { key: "profit", label: t("cols.profit"), value: DASH, note: DASH },
        { key: "roi", label: t("cols.roi"), value: DASH, note: DASH },
      ]
    : [
        {
          key: "clicks",
          label: t("cols.clicks"),
          value: formatInt(v?.clicks),
          delta: cardDelta("clicks", "count", v?.clicks, prev?.clicks),
          note: v?.bots_pct !== undefined ? t("card.kpi.botsCut", { pct: formatPct(v.bots_pct) }) : undefined,
        },
        { key: "leads", label: t("cols.leads"), value: formatInt(v?.leads), delta: cardDelta("leads", "count", v?.leads, prev?.leads), note: t("kpi.cr", { cr: formatPct(v?.cr) }) },
        { key: "conversions", label: t("cols.approved"), value: formatInt(v?.conversions), note: t("kpi.approve", { pct: formatPct(v?.approve_rate, { digits: 0 }) }) },
        {
          key: "hold",
          label: t("kpi.hold"),
          value: formatInt(v?.conversions_hold),
          // «в пути» is the registry's revenue_in_transit, not multiplied here.
          note: v?.revenue_in_transit !== undefined ? t("card.kpi.inTransit", { sum: formatMoney(v.revenue_in_transit) }) : undefined,
        },
        { key: "revenue", label: t("cols.revenue"), value: formatMoney(v?.revenue), delta: cardDelta("revenue", "count", v?.revenue, prev?.revenue), note: t("kpi.epc", { epc: formatMoney(v?.epc, { unit: true }) }) },
        { key: "cost", label: t("cols.cost"), value: formatMoney(v?.cost), delta: cardDelta("cost", "count", v?.cost, prev?.cost), note: t("kpi.cpa", { cpa: formatMoney(v?.cpa, { unit: true }) }) },
        {
          key: "profit",
          label: t("cols.profit"),
          value: formatMoney(v?.profit, { signed: true }),
          delta: cardDelta("profit", "profit", v?.profit, prev?.profit),
          note: prev ? t("kpi.was", { value: formatMoney(prev.profit, { signed: true }) }) : undefined,
          tone: signedTone(v?.profit),
        },
        { key: "roi", label: t("cols.roi"), value: formatPct(v?.roi, { digits: 0, signed: true }), delta: cardDelta("roi", "pp", v?.roi, prev?.roi), tone: signedTone(v?.roi) },
      ];
  return (
    <div className="grid shrink-0 grid-cols-4 overflow-hidden rounded-lg border border-border bg-card xl:grid-cols-8" aria-label={t("card.kpi.aria")} role="group">
      {tiles.map((k) => (
        <MetricCard
          labelCase="none"
          key={k.key}
          label={k.label}
          value={k.value}
          delta={k.delta}
          comparison={k.note}
          tone={k.tone}
          loading={loading && !empty}
          className="min-w-0 rounded-none border-0 border-l border-border px-3.5 py-2.5 first:border-l-0"
        />
      ))}
    </div>
  );
}
